import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import { ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { getNotificationIconConfig } from './notification-icon-config';
import type { AppNotification, NotificationType } from '@socialflows/shared-lib';

type NotificationItemProps = {
  notification: AppNotification;
  onRead: (id: string) => void;
};

const ENTITY_ROUTES: Record<string, (id: string, type: string) => string> = {
  order: (id) => `/e-commerce/orders/${id}`,
  product: (id) => `/e-commerce/products/${id}`,
  ticket: () => `/social-manager/inbox`,
  post: () => `/social-manager/publisher/post-groups`,
  channel: () => `/settings/channels`,
  workflow: (id) => `/settings/workflows/${id}`,
};

const TYPE_LABELS: Record<string, string> = {
  ORDER_CREATED: 'Order',
  ORDER_STATUS_CHANGED: 'Order',
  ORDER_CANCELLED: 'Order',
  PAYMENT_FAILED: 'Payment',
  DELIVERY_EXCEPTION: 'Delivery',
  LOW_STOCK: 'Inventory',
  OUT_OF_STOCK: 'Inventory',
  TICKET_CREATED: 'Ticket',
  TICKET_ASSIGNED: 'Ticket',
  POST_PUBLISHED: 'Post',
  POST_PUBLISH_FAILED: 'Post',
  REPLY_FAILED: 'Reply',
  CHANNEL_DISCONNECTED: 'Channel',
  WORKFLOW_FAILED: 'Workflow',
};

const TYPE_BADGE_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  ORDER_CANCELLED: 'destructive',
  PAYMENT_FAILED: 'destructive',
  OUT_OF_STOCK: 'destructive',
  POST_PUBLISH_FAILED: 'destructive',
  REPLY_FAILED: 'destructive',
  CHANNEL_DISCONNECTED: 'destructive',
  DELIVERY_EXCEPTION: 'outline',
  LOW_STOCK: 'outline',
  WORKFLOW_FAILED: 'outline',
  ORDER_CREATED: 'secondary',
  ORDER_STATUS_CHANGED: 'secondary',
  TICKET_CREATED: 'secondary',
  TICKET_ASSIGNED: 'secondary',
  POST_PUBLISHED: 'secondary',
};

const PRIORITY_BORDER: Record<string, string> = {
  high: 'border-l-destructive',
  medium: 'border-l-yellow-500',
  low: '',
};

export function NotificationItem({ notification, onRead }: NotificationItemProps) {
  const navigate = useNavigate();
  const config = getNotificationIconConfig(notification.type);
  const Icon = config.icon;
  const hasEntity = !!(notification.entityType && notification.entityId);

  const handleClick = () => {
    if (!notification.isRead) {
      onRead(notification.id);
    }
    if (hasEntity) {
      const routeBuilder = ENTITY_ROUTES[notification.entityType!];
      if (routeBuilder) {
        navigate(routeBuilder(notification.entityId!, notification.type));
      }
    }
  };

  const createdAt = new Date(notification.createdAt);
  const relativeTime = formatDistanceToNow(createdAt, { addSuffix: true });
  const fullTime = format(createdAt, 'PPpp');

  return (
    <div
      className={cn(
        'group relative flex gap-3 px-4 py-3 transition-all cursor-pointer border-l-2',
        'hover:bg-accent/40',
        !notification.isRead && 'bg-primary/[0.03]',
        PRIORITY_BORDER[notification.priority] || 'border-l-transparent'
      )}
      onClick={handleClick}
    >
      <div className="flex flex-col items-center pt-0.5 shrink-0">
        <div
          className={cn(
            'flex size-9 items-center justify-center rounded-lg',
            config.bg
          )}
        >
          <Icon className={cn('size-4', config.color)} />
        </div>
      </div>

      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Badge
            variant={TYPE_BADGE_VARIANT[notification.type] ?? 'secondary'}
            className="h-4 px-1.5 text-[9px] font-medium tracking-wide uppercase shrink-0"
          >
            {TYPE_LABELS[notification.type] ?? 'System'}
          </Badge>
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-[10px] text-muted-foreground/60 ml-auto shrink-0 tabular-nums">
                  {relativeTime}
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={4}>
                <p>{fullTime}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {!notification.isRead && (
            <span className="size-1.5 rounded-full bg-blue-500 shrink-0" />
          )}
        </div>

        <p
          className={cn(
            'text-[13px] leading-snug',
            !notification.isRead
              ? 'font-semibold text-foreground'
              : 'font-medium text-muted-foreground'
          )}
        >
          {notification.title}
        </p>

        <p className="text-xs text-muted-foreground/80 line-clamp-2 leading-relaxed">
          {notification.message}
        </p>

        {hasEntity && (
          <span className="flex items-center gap-1 text-[10px] text-muted-foreground/50 mt-0.5 group-hover:text-primary transition-colors w-fit">
            <ExternalLink className="size-2.5" />
            View details
          </span>
        )}
      </div>
    </div>
  );
}

export function NotificationItemSeparator() {
  return <Separator />;
}
